import React from 'react';
import { Form } from 'formik';
import Yup from 'yup';

import createForm, { TextField, ResourceField, CheckField, FormActions } from '../shared/forms';
import UserDefinitions from './UserDefinitions';

const resourceName = 'user';

const roleOptions = () => {
  const options = UserDefinitions.roles.options;
  return typeof options === 'function' ? options() : options;
};

export const roleDescriptions = {
  ROLE_ADMIN: 'Toegang tot alle sollicitaties, gebruikers en instellingen zonder restricties.',
  ROLE_MANAGER: 'Toegang tot alle sollicitaties, kan gebruikers maken en bewerken.',
  ROLE_RECRUITER:
    'Toegang tot sollicitaties en medewerkers binnen de toegewezen locaties. Kan geen gebruikers beheren.'
};

export const selectUserRole = (setFieldValue, role) => {
  setFieldValue('roles', [role]);
  if (role !== 'ROLE_RECRUITER') {
    setFieldValue('locations', []);
  }
};

export const userValidationSchema = Yup.object().shape(
  Object.keys(UserDefinitions).reduce((result, field) => {
    if (UserDefinitions[field].validation) {
      result[field] = UserDefinitions[field].validation;
    }
    return result;
  }, {})
).test(
  'userRoleRequirements',
  'Ongeldige combinatie van rol en locaties',
  function(values) {
    const roles = values && Array.isArray(values.roles) ? values.roles : [];

    if (
      roles.indexOf('ROLE_RECRUITER') >= 0 &&
      (!Array.isArray(values.locations) || values.locations.length === 0)
    ) {
      return this.createError({
        path: 'locations',
        message: 'Minimaal één locatie is verplicht voor een recruitment manager'
      });
    }

    if (values && values.active && (roles.length !== 1 || roles[0] === 'ROLE_USER')) {
      return this.createError({
        path: 'roles',
        message: 'Kies eerst een rol voordat je deze gebruiker activeert'
      });
    }

    return true;
  }
);

export default createForm({
  resourceName,

  redirectPath: () => 'users',
  submitPath: () => 'users',
  updatePath: (props) => `users/${props.match.params.userId}`,

  mapPropsToValues: (props) => {
    const user = props.update || {};
    return {
      firstname: user.firstname || UserDefinitions.firstname.initial,
      surname_prefix: user.surname_prefix || UserDefinitions.surname_prefix.initial,
      surname: user.surname || UserDefinitions.surname.initial,
      email: user.email || UserDefinitions.email.initial,
      google_id: user.google_id || UserDefinitions.google_id.initial,
      phone_number: user.phone_number || UserDefinitions.phone_number.initial,
      outgoing_email: user.outgoing_email || UserDefinitions.outgoing_email.initial,
      outgoing_phone_number: user.outgoing_phone_number || UserDefinitions.outgoing_phone_number.initial,
      locations: (user.locations || []).map((location) => location.id),
      active: user.active !== false,
      roles: user.roles || UserDefinitions.roles.initial
    };
  },

  submitValues: (values) => values,

  validationSchema: userValidationSchema,

  form: (props) => {
    const roles = props.values.roles || [];
    const availableRoles = roleOptions();
    const selectRole = (role) => {
      selectUserRole(props.setFieldValue, role);
    };

    return (
      <Form className="form">
        {props.status && (
          <div className={`alert alert-${props.status.type}`}>{props.status.text}</div>
        )}

        <div className="form-row">
          <TextField
            className="col-sm-4"
            autoFocus
            props={props}
            definitions={UserDefinitions}
            name="firstname"
          />
          <TextField
            className="col-sm-3"
            props={props}
            definitions={UserDefinitions}
            name="surname_prefix"
          />
          <TextField
            className="col-sm-5"
            props={props}
            definitions={UserDefinitions}
            name="surname"
          />
        </div>
        <TextField props={props} definitions={UserDefinitions} name="email" />
        <TextField props={props} definitions={UserDefinitions} name="google_id" />
        <TextField props={props} definitions={UserDefinitions} name="phone_number" />
        {roles.includes('ROLE_RECRUITER') && (
          <ResourceField {...props} definitions={UserDefinitions} name="locations" />
        )}

        <div className="form-group">
          <label>{UserDefinitions.roles.title}</label>
          <div>
            {Object.keys(availableRoles).map((role) => (
              <label className="custom-control custom-radio mb-2" key={role}>
                <input
                  type="radio"
                  name="roles"
                  className="custom-control-input"
                  checked={roles.includes(role)}
                  onChange={() => selectRole(role)}
                />
                <span className="custom-control-label">{availableRoles[role]}</span>
                <small className="d-block text-muted">{roleDescriptions[role]}</small>
              </label>
            ))}
          </div>
        </div>
        <CheckField props={props} name="active" title={UserDefinitions.active.title} />

        <TextField
          props={props}
          definitions={UserDefinitions}
          name="outgoing_email"
          helpText="E-mailadres waarmee emails verstuurd moeten worden."
        />
        <TextField
          props={props}
          definitions={UserDefinitions}
          name="outgoing_phone_number"
          helpText="Telefoon nummer waar de gebruiker mee uit moet bellen."
        />

        <FormActions
          touched={props.dirty}
          update={props.update}
          isSubmitting={props.isSubmitting}
          isValid={props.isValid}
          cancelPath="/users/"
        />
      </Form>
    );
  }
});
